import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon, Settings as SettingsIcon } from '@mui/icons-material';

function Configuracion() {
  const [config, setConfig] = useState({
    porcentaje_indirectos: '17.00',
    porcentaje_financiamiento: '2.00',
    porcentaje_utilidad: '10.00',
    iva: '16.00',
    moneda: 'MXN',
    estado_default: 'Campeche'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Cargar configuración guardada al montar el componente
  useEffect(() => {
    const savedConfig = localStorage.getItem('appConfig');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setConfig(parsedConfig);
      } catch (error) {
        console.error('Error cargando configuración:', error);
      }
    }
  }, []);

  const handleInputChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      // Validar porcentajes
      const porcentajes = ['porcentaje_indirectos', 'porcentaje_financiamiento', 'porcentaje_utilidad', 'iva'];
      for (const campo of porcentajes) {
        const valor = parseFloat(config[campo]);
        if (isNaN(valor) || valor < 0 || valor > 100) {
          throw new Error(`${campo.replace('_', ' ')} debe ser un número entre 0 y 100`);
        }
      }

      // Guardar en localStorage por ahora
      localStorage.setItem('appConfig', JSON.stringify(config));

      setMessage({ type: 'success', text: 'Configuración guardada exitosamente' });

      // Limpiar mensaje después de 3 segundos
      setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <SettingsIcon sx={{ mr: 2, fontSize: 32 }} color="primary" />
        <Typography variant="h4">Configuración del Sistema</Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 4 }}>
        Configura los parámetros de cálculo y configuraciones generales del sistema
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Parámetros de Cálculo" />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Indirectos (%)"
                    type="number"
                    value={config.porcentaje_indirectos}
                    onChange={(e) => handleInputChange('porcentaje_indirectos', e.target.value)}
                    helperText="Porcentaje de costos indirectos aplicado a los PU"
                    InputProps={{
                      inputProps: { min: 0, max: 100, step: 0.01 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Financiamiento (%)"
                    type="number"
                    value={config.porcentaje_financiamiento}
                    onChange={(e) => handleInputChange('porcentaje_financiamiento', e.target.value)}
                    helperText="Porcentaje de costo por financiamiento"
                    InputProps={{
                      inputProps: { min: 0, max: 100, step: 0.01 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Utilidad (%)"
                    type="number"
                    value={config.porcentaje_utilidad}
                    onChange={(e) => handleInputChange('porcentaje_utilidad', e.target.value)}
                    helperText="Porcentaje de utilidad aplicado"
                    InputProps={{
                      inputProps: { min: 0, max: 100, step: 0.01 }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="IVA (%)"
                    type="number"
                    value={config.iva}
                    onChange={(e) => handleInputChange('iva', e.target.value)}
                    helperText="Porcentaje de IVA aplicado"
                    InputProps={{
                      inputProps: { min: 0, max: 100, step: 0.01 }
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Configuración General" />
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Moneda"
                    value={config.moneda}
                    onChange={(e) => handleInputChange('moneda', e.target.value)}
                    helperText="Moneda utilizada en los cálculos"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Estado por Defecto"
                    value={config.estado_default}
                    onChange={(e) => handleInputChange('estado_default', e.target.value)}
                    helperText="Estado/región por defecto para nuevos presupuestos"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Información del Sistema" />
            <Divider />
            <CardContent>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>Base de Datos:</strong> Supabase - Proyecto Geckofy Data 1
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>Región:</strong> Campeche, México
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>Materiales en Catálogo:</strong> 22+ materiales de construcción
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                <strong>Última Actualización:</strong> {new Date().toLocaleDateString('es-MX')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading}
          size="large"
        >
          {loading ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </Box>
    </Box>
  );
}

export default Configuracion;